import { Extension } from "@tiptap/core";
import { Plugin, PluginKey } from "@tiptap/pm/state";
import { DecorationSet } from "@tiptap/pm/view";
import { ySyncPluginKey } from "@tiptap/y-tiptap";

import { createLogger } from "@/utils/logger";

import { processCurrentTextBlock } from "./latex-input/processCurrentTextBlock";
import { scanAndConvertLatexOnInit } from "./latex-input/scanAndConvertLatexOnInit";
import { scanDocumentForLatex } from "./latex-input/scanDocumentForLatex";
import {
  convertLatexToExpression,
  shouldExcludeNodeFromLatexConversion,
} from "./latex-input/utils";

import type { LatexPair } from "./latex-input/types";
import type { DocumentModel } from "@/models/DocumentModel";
import type { EditorState, Transaction } from "@tiptap/pm/state";

export const latexInputPluginKey = new PluginKey<LatexInputState>("latexInput");

export type LatexInputOptions = {
  doc: DocumentModel;
};

export type PendingConversion = {
  content: string;
  start: LatexPair["start"];
  end: LatexPair["end"];
  focusTarget: "before" | "after" | null;
};

export type LatexInputState = {
  decorations: DecorationSet;
  activeInput: LatexPair | null;
  lastCursorPos: number | null;
  pendingConversions: PendingConversion[];
};

const logger = createLogger("LatexInput");

/**
 * Extension that allows typing LaTeX expressions inline and converting them to expression nodes.
 *
 * Supports both single "$" and double "$$" markers:
 * - "$" matches with "$" (ignoring "$$" in between)
 * - "$$" matches with "$$" (ignoring "$" in between)
 *
 * Usage:
 * 1. Type "$" or "$$" to start a LaTeX input
 * 2. Type your LaTeX expression (e.g., "x^2 + y")
 * 3. Type matching "$" or "$$" to close and convert to expression node
 *
 * Visual feedback:
 * - Opening and closing markers are grayed out
 * - Text between markers has blue underline when cursor is inside
 * - Invalid LaTeX shows red underline
 */
export const LatexInput = Extension.create<LatexInputOptions>({
  name: "latexInput",

  addProseMirrorPlugins() {
    const { doc: docModel } = this.options;

    return [
      new Plugin<LatexInputState>({
        key: latexInputPluginKey,

        state: {
          init() {
            return {
              decorations: DecorationSet.empty,
              activeInput: null,
              lastCursorPos: null,
              pendingConversions: [],
            };
          },

          apply(
            tr: Transaction,
            state: LatexInputState,
            oldState: EditorState,
            newState: EditorState,
          ): LatexInputState {
            // Clear pending conversions if meta flag is set
            if (tr.getMeta("clearPendingConversions")) {
              return {
                ...state,
                pendingConversions: [],
              };
            }

            if (oldState.doc.nodeSize <= 4) {
              return {
                decorations: scanDocumentForLatex(tr.doc),
                activeInput: null,
                lastCursorPos: null,
                pendingConversions: [],
              };
            }

            const { doc, selection } = newState;
            const { $from } = selection;

            const parentStart = $from.start();
            const parentEnd = $from.end();
            const cursorPos = parentStart + $from.parentOffset;

            let decorations = state.decorations.map(tr.mapping, tr.doc);

            if (!tr.docChanged && !tr.selectionSet) {
              if (state.activeInput) {
                if (
                  cursorPos < state.activeInput.start.pos ||
                  (state.lastCursorPos != null && cursorPos > state.lastCursorPos)
                ) {
                  return {
                    decorations,
                    activeInput: null,
                    lastCursorPos: null,
                    pendingConversions: state.pendingConversions,
                  };
                }
              }
              return {
                decorations,
                activeInput: state.activeInput,
                lastCursorPos: state.lastCursorPos,
                pendingConversions: state.pendingConversions,
              };
            }

            if (
              tr.getMeta(ySyncPluginKey) &&
              state.decorations.find().length > 0 &&
              decorations.find().length === 0
            ) {
              decorations = scanDocumentForLatex(tr.doc);
            }

            if (shouldExcludeNodeFromLatexConversion($from.parent)) {
              return {
                decorations,
                activeInput: null,
                lastCursorPos: null,
                pendingConversions: state.pendingConversions,
              };
            }

            // Accumulate pending conversions
            const pendingConversions: PendingConversion[] = [];

            // Check if we need to re-process the previous activeInput location
            // (e.g., user clicked outside after editing)
            if (state.activeInput) {
              const { start, end } = state.activeInput;
              const prevInputStart = start.pos;
              const prevInputEnd = end ? end.pos + end.type.length : parentEnd;

              // If cursor moved outside the previous active input range
              if (cursorPos < prevInputStart || cursorPos > prevInputEnd) {
                // Find the text block containing the previous active input
                const prevPos = doc.resolve(prevInputStart);
                if (
                  !shouldExcludeNodeFromLatexConversion(prevPos.parent) &&
                  !prevPos.sameParent($from)
                ) {
                  const prevParentStart = prevPos.start();
                  const prevParentEnd = prevPos.end();

                  // Remove decorations in the previous text block
                  decorations = decorations.remove(
                    decorations.find(prevParentStart, prevParentEnd),
                  );

                  // Re-process the previous text block (cursor is now outside, so it may queue conversion)
                  const result = processCurrentTextBlock(
                    prevPos.parent,
                    prevParentStart,
                    prevParentEnd,
                    cursorPos, // Cursor position (now outside)
                    cursorPos,
                    doc,
                    docModel,
                  );

                  // Add decorations for previous block
                  decorations = decorations.add(doc, result.decorations);
                  pendingConversions.push(...result.pendingConversions);
                }
              }
            }

            // Remove decorations in the current text block range
            decorations = decorations.remove(decorations.find(parentStart, parentEnd));

            const endPos =
              state.lastCursorPos && state.lastCursorPos > cursorPos
                ? state.lastCursorPos
                : cursorPos;

            // Process current text block and get new decorations
            const result = processCurrentTextBlock(
              $from.parent,
              parentStart,
              parentEnd,
              cursorPos,
              endPos,
              doc,
              docModel,
            );

            // Add new decorations for current block
            decorations = decorations.add(doc, result.decorations);
            pendingConversions.push(...result.pendingConversions);

            return {
              decorations,
              activeInput: result.activeInput,
              lastCursorPos: result.activeInput ? endPos : null,
              pendingConversions,
            };
          },
        },

        props: {
          decorations(state) {
            return latexInputPluginKey.getState(state)?.decorations ?? DecorationSet.empty;
          },
        },

        view(editorView) {
          // Scan document on mount to convert any $$...$$ patterns
          scanAndConvertLatexOnInit(editorView, docModel);

          return {
            update(view) {
              const pluginState = latexInputPluginKey.getState(view.state);
              if (!pluginState || pluginState.pendingConversions.length === 0) {
                return;
              }

              // Process all pending conversions
              for (const conversion of pluginState.pendingConversions) {
                if (conversion.end) {
                  try {
                    convertLatexToExpression(
                      conversion.content,
                      conversion.start,
                      conversion.end,
                      docModel,
                      conversion.focusTarget,
                    );
                  } catch {
                    logger.debug("Failed to convert LaTeX", {
                      content: conversion.content,
                      start: conversion.start.pos,
                      end: conversion.end?.pos,
                    });
                  }
                }
              }

              view.dispatch(view.state.tr.setMeta("clearPendingConversions", true));
            },
          };
        },
      }),
    ];
  },
});
